import * as z from 'zod';
import { DtoOrderOrderTypeEnum } from '@repo/api-types/order-retrieval';
import { assetTypes } from '@repo/types/asset';

const assetType = z.enum(assetTypes);
const orderType = z.enum(DtoOrderOrderTypeEnum);

const messageEnableToggle = z.object({
    enable: z.boolean(),
});

const messageAudienceConfig = z.object({
    add: messageEnableToggle.optional(),
    edit: messageEnableToggle.optional(),
    read: messageEnableToggle.optional(),
});

const messagesFeatureConfig = z.object({
    INTERNAL: messageAudienceConfig.optional(),
    EXTERNAL: messageAudienceConfig.optional(),
    TSP_CUST: messageAudienceConfig.optional(),
});

const featureRule = z.object({
    assetType,
    orderTypes: z.array(orderType).optional(),
    statuses: z.array(z.string()).optional(),
});

export type FeatureRule = z.infer<typeof featureRule>;

const featureToggle = z.object({
    enable: z.boolean(),
    enabledFor: z.array(featureRule).optional(),
    permissions: z.array(z.string()).nullable().optional(),
});

export type FeatureToggle = z.infer<typeof featureToggle>;

const questionnaireFeatureConfig = z.object({
    read: featureToggle.optional(),
    write: featureToggle.optional(),
});

const classicSaljaPaths = z.object({
    deleteDraft: z.string(),
    modifyOrder: z.string(),
    downloadAttachment: z.string(),
    orderOutput: z.string(),
    orderRetrieval: z.string(),
    draftRetrieval: z.string(),
    coworkerPreferences: z.string(),
    storeDetails: z.string(),
    storePreferences: z.string(),
    uploadAttachment: z.string(),
    modifyOrderPaymentDate: z.string(),
    services: z.string(),
});

export type ClassicSaljaPaths = z.infer<typeof classicSaljaPaths>;

const orderTakingPaths = z.object({
    sendMessage: z.string(),
    orderMessages: z.string(),
    getAssets: z.string(),
    getOrderDetails: z.string(),
    getDraftDetails: z.string(),
    getQuotationDetails: z.string(),
    deleteQuotation: z.string(),
    quotationRetrieval: z.string(),
    deleteDraft: z.string(),
    draftRetrieval: z.string(),
    coworker: z.string(),
    docusign: z.string(),
    trpc: z.string(),
});

export type OrderTakingPaths = z.infer<typeof orderTakingPaths>;

const orderTakingBffPaths = z.object({
    extraNeedsRequest: z.string(),
});

export type OrderTakingBffPaths = z.infer<typeof orderTakingBffPaths>;

export interface ApiServiceConfig<Paths extends Record<keyof Paths, string>> {
    baseUrl: string;
    paths: Paths;
    pathBaseUrls?: Partial<Record<keyof Paths, string>> | undefined;
}

const createApiServiceConfig = <Paths extends z.ZodObject>(paths: Paths) =>
    z.object({
        baseUrl: z.string(),
        paths,
        pathBaseUrls: z.partialRecord(paths.keyof(), z.string()).optional(),
    });

const apiConfig = z.object({
    services: z.object({
        classicSalja: createApiServiceConfig(classicSaljaPaths),
        orderTaking: createApiServiceConfig(orderTakingPaths),
        orderTakingBff: createApiServiceConfig(orderTakingBffPaths),
    }),
});

export type ApiConfig = z.infer<typeof apiConfig>;

const fragmentFeatures = z.object({
    orderHistory: z.boolean(),
    manualPayments: z.boolean(),
});

export type FragmentFeatures = z.infer<typeof fragmentFeatures>;

const enableToggle = z.object({
    enable: z.boolean(),
});

const features = z.object({
    fragments: fragmentFeatures.optional(),
    articleDeliveryView: enableToggle.optional(),
    articleServiceView: enableToggle.optional(),
    extraNeedRequest: enableToggle.optional(),
    paymentsRims: z
        .object({
            enable: z.boolean(),
            url: z.string().optional(),
        })
        .optional(),
    orderHistory: enableToggle.optional(),
    priceExclTax: enableToggle.optional(),
    paymentLinkForRegisteredCustomersOnly: enableToggle.optional(),
    kitchenLink: enableToggle.optional(),
    messages: messagesFeatureConfig.optional(),
    draftV2Service: enableToggle.optional(),
    docusign: z
        .object({
            enable: z.boolean(),
            enabledFor: z.array(featureRule).optional(),
            permissions: z.array(z.string()).nullable().optional(),
            acceptedFileTypes: z.array(z.string()),
        })
        .optional(),
    redirectToCartOnAssetShare: enableToggle.optional(),
    showPriceSummary: enableToggle.optional(),
    pickingListPdf: featureToggle.optional(),
    attachments: z
        .object({
            add: z
                .object({
                    enableFor: z.array(assetType),
                    acceptedFileTypes: z.string().optional(),
                })
                .optional(),
            edit: z.object({ enableFor: z.array(assetType) }).optional(),
            remove: z.object({ enableFor: z.array(assetType) }).optional(),
            editComments: z.object({ enableFor: z.array(assetType) }).optional(),
            maxSizeMb: z.number().optional(),
        })
        .optional(),
    commandMenu: enableToggle.optional(),
    enableCustomerMessagingSmsSharing: z.boolean().optional(),
    enableCustomerMessagingDraftEmailSharing: z.boolean().optional(),
    customerInformation: enableToggle.optional(),
    services: z
        .object({
            timeWindowSelection: z
                .object({
                    delivery: featureToggle.optional(),
                    providedService: featureToggle.optional(),
                    disabledServiceIds: z.array(z.string()).optional(),
                })
                .optional(),
        })
        .optional(),
    questionnaire: questionnaireFeatureConfig.optional(),
    paymentDateModification: z
        .object({
            read: featureToggle.optional(),
            edit: featureToggle.optional(),
        })
        .optional(),
});

export type Features = z.infer<typeof features>;

const shareOptions = z.object({
    smsEnabledFor: z.object({
        assetTypes: z.array(assetType),
        orderTypes: z.array(orderType),
    }),
    emailEnabledFor: z.array(assetType),
    printEnabledFor: z.array(assetType),
});

const priceFormat = z.object({
    useCurrencySymbolMap: z.boolean(),
});

export const configSchema = z.object({
    api: apiConfig,
    addressFormatStandard: z.string(),
    addressFormatPuP: z.string(),
    dateTimeFormat: z.string(),
    dateFormat: z.string(),
    timeFormat: z.string(),
    priceFormat,
    measurementSystem: z.enum(['metric', 'imperial']),
    weekStartsOn: z
        .union([z.literal(0), z.literal(1), z.literal(2), z.literal(3), z.literal(4), z.literal(5), z.literal(6)])
        .optional(),
    cartDomain: z.string(),
    cartPath: z.string(),
    cartHomePath: z.string(),
    feature: features,
    shareOptions,
    filters: z.object({
        noOfDays: z.object({
            enable: z.boolean(),
            default: z.string(),
            options: z.array(z.string()),
        }),
    }),
});

export type Config = z.infer<typeof configSchema>;
